import { DATE_FORMAT_RFC } from 'common/constants'
import { tryCallApi } from 'common/helpers'
import { AccountingPeriodsService } from 'common/open-api'
import { isEqual } from 'lodash'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { selectTermsData } from 'store/settings/accountingPeriod/selectors'

export type TermYearMonth = { year: number; month: number }

type Fetched = { date: string; termMonth?: { term_id: number; month: number } }

// Accounting term year/month of a date: fetch the term month by date (term_id + month),
// then take the year from the matching term in the store.
// `isLoading` is true until the request settles so callers can hold the message instead of flashing the fallback.
// A failed request settles with no result; callers then fall back to the calendar month.
export const useTermYearMonthByDate = (date?: string | null) => {
  const terms = useSelector(selectTermsData, isEqual)
  const [fetched, setFetched] = useState<Fetched>()

  useEffect(() => {
    if (!date) return
    let cancelled = false
    const fetchTermMonth = async () => {
      const res = await tryCallApi(AccountingPeriodsService.postAccountingPeriodsTermMonthByDate)({
        'filter[time_date]': moment(date).format(DATE_FORMAT_RFC),
      })
      if (!cancelled) setFetched({ date, termMonth: res.data?.data })
    }
    fetchTermMonth()
    return () => {
      cancelled = true
    }
  }, [date])

  const isLoading = !!date && fetched?.date !== date
  const termMonth = date && fetched?.date === date ? fetched.termMonth : undefined
  const termYear = terms?.find(t => t.id === termMonth?.term_id)?.year
  const termYearMonth: TermYearMonth | undefined =
    termYear && termMonth ? { year: termYear, month: termMonth.month } : undefined

  return { termYearMonth, isLoading }
}
